function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { INPUT } from "../../components/form-styles";
import { VoicePicker, loadVoices, voiceList, playAudio, stopAudio, playingKey, subscribePlaying } from "./voices";
import { SIZES } from "./sequence-common";
import * as api from "./sequences-api";
import { isBroken, markBroken, onBroken } from "./broken-images";
import { BrandLibraryPane, damAvailable } from "./brand-library";

/*
 * Sequence start (PlunkDocs specs/SEQUENCES.md §13.1). AssetPicker: the shared asset grid in pick order, used by the
 * clip page and the storyboard's "Add from Assets". SequenceCreateTab: the /Content/sequences/new page body (redesign
 * 2026-10-08, see the note above the class). "Write the first cut" posts POST /v1/content/sequences
 * { writeFirstCut: true } and hands the new sequence to the parent, which opens the editor. Writing the cut costs
 * nothing; no credit amounts anywhere (DS 2026-10-01).
 */

const toast = api.toast;
const FILTER_ACCEPT = "video/mp4,video/quicktime,video/webm";

/**
 * Asset picker in pick order. Props: picks (array of asset ids, in order), onChange(picks), max (40), disabled,
 * assets (optional preloaded list; loads GET /content/assets itself when absent), onAssets(list) once loaded, compact.
 * Also used by the storyboard's "Add from Assets".
 */
export class AssetPicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      assets: this.props.assets || undefined,
      q: "",
      bucket: "",
      upBusy: false,
      upPct: 0,
      library: false,
      hasLibrary: false
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "load", async () => {
      const list = await api.listAssets({
        $top: 400
      });
      if (!this._alive) return;
      this.setState({
        assets: list === null ? null : list
      });
      if (this.props.onAssets) this.props.onAssets(list || []);
    });
    _defineProperty(this, "toggle", id => {
      const {
        picks,
        onChange,
        max = api.MAX_PICKS,
        single
      } = this.props;
      if (single) {
        onChange(picks.includes(id) ? [] : [id]);
        return;
      }
      if (picks.includes(id)) onChange(picks.filter(x => x !== id));else if (picks.length >= max) toast(`Up to ${max} pieces in one sequence.`);else onChange([...picks, id]);
    });
    _defineProperty(this, "upload", async files => {
      const accept = this.props.accept || "video";
      const ok = accept === "any" ? /^(image|video)\// : accept === "image" ? /^image\// : /^video\//;
      const list = Array.from(files || []).filter(f => ok.test(f.type || "")).slice(0, 5);
      if (!list.length) {
        toast(accept === "image" ? "Pick an image: JPG, PNG or WEBP." : accept === "any" ? "Pick an image or a video." : "Pick a video file: a screen recording as mp4, mov or webm.");
        return;
      }
      this.setState({
        upBusy: true,
        upPct: 0
      });
      const added = [];
      for (const f of list) {
        try {
          const isVideo = /^video\//.test(f.type || "");
          const a = await api.uploadAsset(f, p => {
            if (this._alive) this.setState({
              upPct: p
            });
          }, isVideo ? {
            tags: "recording"
          } : {});
          if (a && a.id) added.push(isVideo ? {
            ...a,
            source: "recording"
          } : {
            ...a,
            kind: "image",
            source: "upload"
          });
        } catch (e) {
          toast(`${f.name}: ${e && e.message || "upload failed"}`);
        }
      }
      if (!this._alive) return;
      const assets = [...added, ...(this.state.assets || [])];
      const addedBucket = added.length ? api.assetBucket(added[0]) : this.state.bucket;
      this.setState({
        assets,
        upBusy: false,
        upPct: 0,
        bucket: this.props.only && !this.props.only.includes(addedBucket) ? "" : addedBucket
      });
      if (this.props.onAssets) this.props.onAssets(assets);
      if (added.length) {
        const ids = added.map(a => a.id);
        this.props.onChange(this.props.single ? [ids[0]] : [...this.props.picks, ...ids].slice(0, this.props.max || api.MAX_PICKS));
        toast(`${added.length} ${added[0].kind === "video" ? "recording" : "image"}${added.length === 1 ? "" : "s"} added and picked.`);
      }
    });
    _defineProperty(this, "fromLibrary", a => {
      const assets = [a, ...(this.state.assets || []).filter(x => x.id !== a.id)];
      this.setState({
        assets,
        library: false,
        bucket: ""
      });
      if (this.props.onAssets) this.props.onAssets(assets);
      const picks = this.props.picks.includes(a.id) ? this.props.picks : this.props.single ? [a.id] : [...this.props.picks, a.id].slice(0, this.props.max || api.MAX_PICKS);
      this.props.onChange(picks);
      toast(a.reused ? "Picked from the brand library." : "Added from the brand library and picked.");
    });
  }
  componentDidMount() {
    damAvailable().then(ok => {
      if (this._alive && ok) this.setState({
        hasLibrary: true
      });
    });
    if (!this.state.assets) this.load();
    this._unsubBroken = onBroken(() => {
      if (this._alive) this.forceUpdate();
    });
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._unsubBroken) this._unsubBroken();
  }
  render() {
    const {
      picks,
      disabled,
      compact,
      hint,
      only,
      exclude,
      accept
    } = this.props;
    const {
      assets,
      q,
      bucket,
      upBusy,
      upPct,
      library,
      hasLibrary
    } = this.state;
    const ql = q.trim().toLowerCase();
    // A picture whose file can't be loaded can't be used either: leave it out (see broken-images.js).
    const allowed = a => (!only || only.includes(api.assetBucket(a))) && !(exclude || []).includes(a.id) && !(a.kind !== "video" && isBroken(a.latestUrl || a.url));
    const visible = (assets || []).filter(a => allowed(a) && (!bucket || api.assetBucket(a) === bucket) && (!ql || `${a.title || ""} ${a.tags || ""} ${a.description || ""} ${a.productTitle || ""}`.toLowerCase().includes(ql)));
    const counts = {};
    (assets || []).filter(allowed).forEach(a => {
      const b = api.assetBucket(a);
      counts[b] = (counts[b] || 0) + 1;
    });
    const buckets = api.BUCKETS.filter(([k]) => !k || !only || only.includes(k));
    const uploadAccept = accept === "image" ? "image/jpeg,image/png,image/webp" : accept === "any" ? "image/jpeg,image/png,image/webp," + FILTER_ACCEPT : FILTER_ACCEPT;
    const uploadLabel = accept === "image" ? "Upload" : accept === "any" ? "Upload" : "Recording";
    const uploadTitle = accept === "video" || !accept ? "Upload a screen recording (mp4, mov or webm). It lands in Assets and is picked here." : "Upload an image. It lands in Assets and is picked here.";
    return /*#__PURE__*/React.createElement("div", {
      className: "sq-picker" + (compact ? " compact" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "sq-picker-tools"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sq-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      value: q,
      placeholder: "Search by title, tag or product",
      disabled: disabled,
      onChange: e => this.setState({
        q: e.target.value
      })
    })), /*#__PURE__*/React.createElement("span", {
      className: "hub-ship-seg"
    }, buckets.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k || "all",
      type: "button",
      className: "territory-toggle xs" + (bucket === k ? " active" : ""),
      disabled: disabled,
      onClick: () => this.setState({
        bucket: k
      })
    }, l, k && counts[k] ? /*#__PURE__*/React.createElement("i", null, counts[k]) : null))), hasLibrary ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs" + (library ? " active" : ""),
      disabled: disabled,
      title: "Pick from the brand library",
      onClick: () => this.setState({
        library: !library
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      style: {
        fontSize: 15,
        verticalAlign: "-3px"
      }
    }, "collections_bookmark"), " Brand library") : null, /*#__PURE__*/React.createElement("label", {
      className: "territory-toggle xs sq-upload" + (upBusy ? " on" : ""),
      title: uploadTitle
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: uploadAccept,
      multiple: !this.props.single,
      disabled: disabled || upBusy,
      onChange: e => {
        this.upload(e.target.files);
        e.target.value = "";
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "upload"), upBusy ? `Uploading ${upPct}%` : uploadLabel), hint ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, hint) : null), library ? /*#__PURE__*/React.createElement(BrandLibraryPane, {
      accept: accept === "image" ? "image" : accept === "video" ? "video" : "any",
      disabled: disabled,
      onPicked: this.fromLibrary
    }) : assets === undefined ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Loading assets\u2026") : assets === null ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Assets are not available on this server yet.") : !visible.length ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, ql || bucket ? "Nothing matches. Clear the search or the filter." : "No images or videos in Assets yet. Upload a few first.") : /*#__PURE__*/React.createElement("div", {
      className: "sq-picker-grid"
    }, visible.map(a => {
      const idx = picks.indexOf(a.id);
      const on = idx !== -1;
      const b = api.assetBucket(a);
      const thumb = a.kind === "video" ? a.posterUrl || a.latestUrl || null : a.latestUrl || a.url;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: a.id,
        className: "sq-pa" + (on ? " on" : ""),
        disabled: disabled,
        title: a.title || "",
        onClick: () => this.toggle(a.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "im" + (b === "recording" ? " contain" : "")
      }, thumb ? /*#__PURE__*/React.createElement("img", {
        src: thumb,
        alt: "",
        loading: "lazy",
        onError: () => {
          if (a.kind !== "video") markBroken(thumb);
        }
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, a.kind === "video" ? "play_circle" : "image")), on ? /*#__PURE__*/React.createElement("i", {
        className: "n"
      }, idx + 1) : null, /*#__PURE__*/React.createElement("span", {
        className: "kind"
      }, b), a.kind === "video" && a.durationSeconds ? /*#__PURE__*/React.createElement("span", {
        className: "dur"
      }, api.fmtClock(a.durationSeconds)) : null, /*#__PURE__*/React.createElement("span", {
        className: "cap"
      }, a.title || (a.url || "").split("/").pop() || a.kind));
    })));
  }
}

/*
 * The "New sequence" page body (create-flow redesign 2026-10-08, PlunkDocs mockups/create-flow-redesign-2026-10-08.html,
 * Sequence screen). A full-width shell like the sequence editor: top bar (back, title, kind switch, summary and the
 * always-visible "Write the first cut"), a main column (brief, pick the pieces, a sticky "Your order" tray that
 * reorders by drag or by the arrow buttons) and a 340 px Settings panel with the defaults filled in. Posts the same
 * POST /v1/content/sequences { writeFirstCut: true } body as before. Classes `sn-`, scoped under .sn-view
 * (sass/v2/_sequence-new.scss). AssetPicker above is left alone: this page draws its own grid from the same data.
 */
const KIND_LABEL = {
  product: "Product",
  image: "Image",
  clip: "Clip",
  recording: "Recording",
  render: "Render"
};
const UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp," + FILTER_ACCEPT;
const thumbOfAsset = a => !a ? null : a.kind === "video" ? a.posterUrl || a.latestUrl || null : a.latestUrl || a.url || null;
const titleOfAsset = a => !a ? "Piece" : a.title || (a.url || "").split("/").pop() || (a.kind === "video" ? "Video" : "Image");
const validPurpose = p => api.PURPOSES.some(([k]) => k === p) ? p : "promo";
export class SequenceCreateTab extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      brief: this.props.brief || "",
      title: "",
      purpose: validPurpose(this.props.purpose),
      picks: (this.props.picks || []).slice(0, api.MAX_PICKS),
      formats: ["16:9", "9:16", "1:1"],
      voice: "Joanna",
      musicUrl: "",
      musicOpen: false,
      voiceOpen: false,
      brandKit: true,
      captions: true,
      target: 45,
      productId: this.props.productId || "",
      busy: false,
      err: "",
      credits: null,
      assets: undefined,
      q: "",
      bucket: "",
      upBusy: false,
      upPct: 0,
      drag: null,
      over: null,
      voices: voiceList(),
      playing: playingKey()
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "briefRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "loadAssets", async () => {
      const list = await api.listAssets({
        $top: 400
      });
      if (this._alive) this.setState({
        assets: list === null ? null : list
      });
    });
    /* ---------- picks ---------- */
    _defineProperty(this, "toggle", id => {
      const {
        picks
      } = this.state;
      if (picks.includes(id)) this.setState({
        picks: picks.filter(x => x !== id)
      });else if (picks.length >= api.MAX_PICKS) toast(`Up to ${api.MAX_PICKS} pieces in one sequence.`);else this.setState({
        picks: [...picks, id],
        err: this.state.err && /Pick at least/.test(this.state.err) ? "" : this.state.err
      });
    });
    _defineProperty(this, "removePick", id => this.setState({
      picks: this.state.picks.filter(x => x !== id)
    }));
    _defineProperty(this, "movePick", (from, to) => {
      const picks = [...this.state.picks];
      if (from === to || from < 0 || to < 0 || from >= picks.length || to >= picks.length) return;
      const [x] = picks.splice(from, 1);
      picks.splice(to, 0, x);
      this.setState({
        picks
      });
    });
    _defineProperty(this, "onDrop", to => {
      const from = this.state.drag;
      this.setState({
        drag: null,
        over: null
      });
      if (from === null || from === undefined) return;
      this.movePick(from, to);
    });
    /* ---------- upload (same path as AssetPicker: videos land as recordings, images as uploads) ---------- */
    _defineProperty(this, "upload", async files => {
      const list = Array.from(files || []).filter(f => /^(image|video)\//.test(f.type || "")).slice(0, 5);
      if (!list.length) {
        toast("Pick an image (JPG, PNG or WEBP) or a video (mp4, mov or webm).");
        return;
      }
      this.setState({
        upBusy: true,
        upPct: 0
      });
      const added = [];
      for (const f of list) {
        try {
          const isVideo = /^video\//.test(f.type || "");
          const a = await api.uploadAsset(f, p => {
            if (this._alive) this.setState({
              upPct: p
            });
          }, isVideo ? {
            tags: "recording"
          } : {});
          if (a && a.id) added.push(isVideo ? {
            ...a,
            source: "recording"
          } : {
            ...a,
            kind: "image",
            source: "upload"
          });
        } catch (e) {
          toast(`${f.name}: ${e && e.message || "upload failed"}`);
        }
      }
      if (!this._alive) return;
      const assets = [...added, ...(this.state.assets || [])];
      const room = Math.max(0, api.MAX_PICKS - this.state.picks.length);
      const ids = added.map(a => a.id).slice(0, room);
      this.setState({
        assets,
        upBusy: false,
        upPct: 0,
        bucket: "",
        picks: [...this.state.picks, ...ids]
      });
      if (added.length) toast(`${added.length} uploaded${ids.length ? " and picked" : ""}.`);
    });
    /* ---------- settings ---------- */
    _defineProperty(this, "toggleFormat", f => {
      const {
        formats
      } = this.state;
      if (formats.includes(f)) {
        if (formats.length === 1) {
          toast("Keep at least one size.");
          return;
        }
        this.setState({
          formats: formats.filter(x => x !== f)
        });
      } else this.setState({
        formats: api.FORMATS.map(([k]) => k).filter(k => k === f || formats.includes(k))
      });
    });
    _defineProperty(this, "submit", async () => {
      const s = this.state;
      if (!s.picks.length) {
        this.setState({
          err: "Pick at least one piece."
        });
        toast("Pick at least one piece.");
        return;
      }
      if (!s.brief.trim() && s.purpose !== "walkthrough") {
        const msg = "Say what the video is for: one or two sentences.";
        this.setState({
          err: msg
        });
        toast(msg);
        try {
          if (this.briefRef.current) this.briefRef.current.focus();
        } catch (e) {/* no focus */}
        return;
      }
      stopAudio();
      this.setBusy(true);
      this.setState({
        err: "",
        credits: null
      });
      try {
        const body = {
          purpose: s.purpose,
          brief: s.brief.trim(),
          assetIds: s.picks,
          formats: s.formats,
          voice: s.voice,
          brandKit: !!s.brandKit,
          captions: !!s.captions,
          writeFirstCut: true,
          settings: {
            targetSeconds: Number(s.target) || null,
            captions: !!s.captions
          }
        };
        if (s.title.trim()) body.title = s.title.trim();
        if (s.musicUrl.trim()) body.musicUrl = s.musicUrl.trim();
        if (s.purpose === "walkthrough" && s.productId.trim()) body.productId = s.productId.trim();
        if (this.props.containerId) body.containerId = String(this.props.containerId);
        if (this.props.storyId) body.storyId = String(this.props.storyId);
        const r = await api.createSequence(body);
        if (!r || !r.sequence) throw new Error("The server returned no sequence.");
        toast(r.segments && r.segments.length ? `First cut written: ${r.segments.length} scenes.` : "Sequence created.");
        if (this.props.onCreated) this.props.onCreated(r.sequence, r);
      } catch (e) {
        const info = api.errInfo(e);
        const msg = info.soft ? "Sequences are not available on this server yet." : info.status === 422 ? info.message || "Pick images or videos only." : info.insufficient ? "Not enough credits." : info.message || "Could not write the first cut.";
        if (this._alive) this.setState({
          err: msg,
          credits: info.insufficient ? {
            buyUrl: info.buyUrl || api.BUY_URL
          } : null
        });
        toast(msg);
      } finally {
        if (this._alive) this.setBusy(false);
      }
    });
  }
  componentDidMount() {
    this.loadAssets();
    this._unsub = subscribePlaying(k => {
      if (this._alive) this.setState({
        playing: k
      });
    });
    this._unsubBroken = onBroken(() => {
      if (this._alive) this.forceUpdate();
    });
    loadVoices().then(voices => {
      if (this._alive) this.setState({
        voices
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._unsub) this._unsub();
    if (this._unsubBroken) this._unsubBroken();
  }
  setBusy(b) {
    this.setState({
      busy: b
    });
    if (this.props.onBusy) this.props.onBusy(b);
  }
  /* ---------- render ---------- */
  renderTop(busy) {
    const s = this.state;
    const n = s.picks.length;
    const reason = !n ? "Pick at least one piece first" : "";
    return /*#__PURE__*/React.createElement("div", {
      className: "sn-top"
    }, this.props.onBack ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-iconbtn",
      "aria-label": "Back to Content",
      title: "Back to Content",
      onClick: this.props.onBack
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "chevron_left")) : null, /*#__PURE__*/React.createElement("div", {
      className: "sn-title"
    }, /*#__PURE__*/React.createElement("h1", null, "New sequence"), /*#__PURE__*/React.createElement("span", null, "A narrated video from pieces you already have")), /*#__PURE__*/React.createElement("span", {
      className: "sn-seg",
      role: "group",
      "aria-label": "Kind of video"
    }, api.PURPOSES.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: s.purpose === k ? "on" : "",
      "aria-pressed": s.purpose === k,
      disabled: busy,
      title: api.PURPOSE_HINT[k],
      onClick: () => this.setState({
        purpose: k
      })
    }, l))), /*#__PURE__*/React.createElement("span", {
      className: "sn-sp"
    }), busy ? /*#__PURE__*/React.createElement("span", {
      className: "sn-progress",
      role: "status"
    }, /*#__PURE__*/React.createElement("i", null), "Writing the first cut\u2026 6 to 15 seconds") : /*#__PURE__*/React.createElement("span", {
      className: "sn-summary" + (n ? "" : " dim"),
      "aria-live": "polite"
    }, n ? `${n} piece${n === 1 ? "" : "s"} · free to write, you render later` : "Pick at least one piece to start"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      className: "sn-go",
      disabled: busy || !n,
      title: reason || "Writes narration and a storyboard. Nothing renders yet.",
      onClick: this.submit
    }, busy ? "Writing…" : "Write the first cut"));
  }
  renderGrid(busy) {
    const s = this.state;
    const {
      assets,
      q,
      bucket
    } = s;
    const ql = q.trim().toLowerCase();
    const usable = a => !(a.kind !== "video" && isBroken(a.latestUrl || a.url)); // see broken-images.js
    const counts = {};
    (assets || []).filter(usable).forEach(a => {
      const b = api.assetBucket(a);
      counts[b] = (counts[b] || 0) + 1;
    });
    const visible = (assets || []).filter(a => usable(a) && (!bucket || api.assetBucket(a) === bucket) && (!ql || `${a.title || ""} ${a.tags || ""} ${a.description || ""} ${a.productTitle || ""}`.toLowerCase().includes(ql)));
    const buckets = api.BUCKETS.filter(([k]) => !k || counts[k] || bucket === k);
    return /*#__PURE__*/React.createElement("section", {
      className: "sn-pick",
      "aria-labelledby": "sn-pick-h"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sn-pickhead"
    }, /*#__PURE__*/React.createElement("h2", {
      id: "sn-pick-h"
    }, "Pick the pieces"), /*#__PURE__*/React.createElement("span", {
      className: "sn-mute"
    }, "in the order you want them"), /*#__PURE__*/React.createElement("span", {
      className: "sn-sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "sn-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      value: q,
      "aria-label": "Search pieces",
      placeholder: "Search by name, tag or product",
      disabled: busy,
      onChange: e => this.setState({
        q: e.target.value
      })
    })), /*#__PURE__*/React.createElement("span", {
      className: "sn-chips",
      role: "group",
      "aria-label": "Filter pieces"
    }, buckets.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k || "all",
      className: "sn-chip" + (bucket === k ? " on" : ""),
      "aria-pressed": bucket === k,
      disabled: busy,
      onClick: () => this.setState({
        bucket: k
      })
    }, l, k && counts[k] ? /*#__PURE__*/React.createElement("i", null, counts[k]) : null)), /*#__PURE__*/React.createElement("label", {
      className: "sn-chip sn-upload" + (s.upBusy ? " busy" : ""),
      title: "Upload images or videos. They land in Assets and are picked here."
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: UPLOAD_ACCEPT,
      multiple: true,
      disabled: busy || s.upBusy,
      onChange: e => {
        this.upload(e.target.files);
        e.target.value = "";
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "upload"), s.upBusy ? `Uploading ${s.upPct}%` : "Upload"))), assets === undefined ? /*#__PURE__*/React.createElement("p", {
      className: "sn-empty"
    }, "Loading your pieces\u2026") : assets === null ? /*#__PURE__*/React.createElement("p", {
      className: "sn-empty"
    }, "Assets are not available on this server yet.") : !visible.length ? /*#__PURE__*/React.createElement("p", {
      className: "sn-empty"
    }, ql || bucket ? /*#__PURE__*/React.createElement(React.Fragment, null, "Nothing matches. ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-link",
      onClick: () => this.setState({
        q: "",
        bucket: ""
      })
    }, "Clear the search and filter")) : "No images or videos in Assets yet. Upload a few to start.") : /*#__PURE__*/React.createElement("div", {
      className: "sn-grid"
    }, visible.map(a => {
      const idx = s.picks.indexOf(a.id);
      const on = idx !== -1;
      const b = api.assetBucket(a);
      const thumb = thumbOfAsset(a);
      const name = titleOfAsset(a);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: a.id,
        className: "sn-piece" + (on ? " on" : ""),
        "aria-pressed": on,
        disabled: busy,
        title: name,
        "aria-label": on ? `${name}, picked as number ${idx + 1}. Click to remove.` : `${name}. Click to pick.`,
        onClick: () => this.toggle(a.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "sn-thumb" + (b === "recording" ? " contain" : "")
      }, thumb ? /*#__PURE__*/React.createElement("img", {
        src: thumb,
        alt: "",
        loading: "lazy",
        onError: () => {
          if (a.kind !== "video") markBroken(thumb);
        }
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons sn-nothumb",
        "aria-hidden": "true"
      }, a.kind === "video" ? "play_circle" : "image"), on ? /*#__PURE__*/React.createElement("i", {
        className: "sn-num",
        "aria-hidden": "true"
      }, idx + 1) : null, /*#__PURE__*/React.createElement("span", {
        className: "sn-kind"
      }, KIND_LABEL[b] || "Image", a.kind === "video" && a.durationSeconds ? ` ${api.fmtClock(a.durationSeconds)}` : "")), /*#__PURE__*/React.createElement("span", {
        className: "sn-cap"
      }, name));
    })));
  }
  renderTray(busy) {
    const s = this.state;
    const byId = {};
    (s.assets || []).forEach(a => {
      byId[a.id] = a;
    });
    const n = s.picks.length;
    const note = s.brandKit ? "Title and end cards from your brand kit are added where they fit." : "";
    const order = s.purpose === "promo" ? "A promo may open on its strongest piece." : "";
    return /*#__PURE__*/React.createElement("div", {
      className: "sn-tray",
      role: "region",
      "aria-label": "Your order"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sn-trayk"
    }, "Your order"), !n ? /*#__PURE__*/React.createElement("span", {
      className: "sn-mute"
    }, "Nothing picked yet. Click a piece above to add it here.") : /*#__PURE__*/React.createElement("ol", {
      className: "sn-order"
    }, s.picks.map((id, i) => {
      const a = byId[id];
      const thumb = thumbOfAsset(a);
      const name = titleOfAsset(a);
      return /*#__PURE__*/React.createElement("li", {
        key: id,
        className: "sn-ochip" + (s.drag === i ? " dragging" : "") + (s.over === i && s.drag !== null && s.drag !== i ? s.drag < i ? " drop-after" : " drop-before" : ""),
        draggable: !busy,
        onDragStart: e => {
          this.setState({
            drag: i
          });
          try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(i));
          } catch (x) {/* old browser */}
        },
        onDragOver: e => {
          if (s.drag === null) return;
          e.preventDefault();
          if (s.over !== i) this.setState({
            over: i
          });
        },
        onDragEnd: () => this.setState({
          drag: null,
          over: null
        }),
        onDrop: e => {
          e.preventDefault();
          this.onDrop(i);
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "sn-ograb material-icons",
        "aria-hidden": "true"
      }, "drag_indicator"), thumb ? /*#__PURE__*/React.createElement("img", {
        src: thumb,
        alt: ""
      }) : /*#__PURE__*/React.createElement("span", {
        className: "sn-othumb material-icons",
        "aria-hidden": "true"
      }, a && a.kind === "video" ? "movie" : "image"), /*#__PURE__*/React.createElement("span", {
        className: "sn-oname"
      }, i + 1, " \xB7 ", name), /*#__PURE__*/React.createElement("span", {
        className: "sn-oacts"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: busy || i === 0,
        "aria-label": `Move ${name} earlier`,
        title: "Move earlier",
        onClick: () => this.movePick(i, i - 1)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "chevron_left")), /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: busy || i === n - 1,
        "aria-label": `Move ${name} later`,
        title: "Move later",
        onClick: () => this.movePick(i, i + 1)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "chevron_right")), /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: busy,
        "aria-label": `Remove ${name}`,
        title: "Remove",
        onClick: () => this.removePick(id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "close"))));
    })), n ? /*#__PURE__*/React.createElement("span", {
      className: "sn-traynote"
    }, [n > 1 ? "Drag or use the arrows to reorder." : "", order, note].filter(Boolean).join(" ")) : null);
  }
  renderSettings(busy) {
    const s = this.state;
    const cur = s.voices.find(v => v.id === s.voice) || null;
    const [vname, ...vrest] = String(cur && cur.label || s.voice).split(" · ");
    const vkey = `voice:${s.voice}`;
    const vplaying = s.playing === vkey;
    const inp = {
      ...INPUT,
      height: "34px",
      padding: "0 10px"
    };
    return /*#__PURE__*/React.createElement("aside", {
      className: "sn-side",
      "aria-labelledby": "sn-set-h"
    }, /*#__PURE__*/React.createElement("h2", {
      id: "sn-set-h"
    }, "Settings"), /*#__PURE__*/React.createElement("div", {
      className: "sn-row stack"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sn-k",
      id: "sn-sizes-k"
    }, "Sizes"), /*#__PURE__*/React.createElement("div", {
      className: "sn-sizes",
      role: "group",
      "aria-labelledby": "sn-sizes-k"
    }, SIZES.map(([k, name, ratio]) => {
      const on = s.formats.includes(k);
      const f = api.FORMATS.find(([x]) => x === k) || [];
      return /*#__PURE__*/React.createElement("label", {
        key: k,
        className: "sn-pill" + (on ? " on" : ""),
        title: f[2] || ""
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: on,
        disabled: busy,
        onChange: () => this.toggleFormat(k)
      }), name, " ", ratio);
    }))), /*#__PURE__*/React.createElement("div", {
      className: "sn-row"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-k",
      htmlFor: "sn-length"
    }, "Length"), /*#__PURE__*/React.createElement("select", {
      id: "sn-length",
      className: "sn-select",
      value: s.target,
      disabled: busy,
      onChange: e => this.setState({
        target: Number(e.target.value)
      })
    }, api.TARGETS.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, l)))), /*#__PURE__*/React.createElement("div", {
      className: "sn-row" + (s.voiceOpen ? " stack" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "sn-k"
    }, "Voice"), s.voiceOpen ? /*#__PURE__*/React.createElement("div", {
      className: "sn-open"
    }, /*#__PURE__*/React.createElement(VoicePicker, {
      labelClass: "sn-voicepick",
      label: "",
      value: s.voice,
      disabled: busy,
      selectStyle: inp,
      onChange: voice => this.setState({
        voice
      })
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-link",
      onClick: () => this.setState({
        voiceOpen: false
      })
    }, "Done")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "sn-v"
    }, vname, vrest.length ? /*#__PURE__*/React.createElement("i", null, " \xB7 ", vrest.join(" · ")) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-mini" + (vplaying ? " on" : ""),
      disabled: !(cur && cur.sampleUrl),
      "aria-label": vplaying ? `Stop the ${vname} sample` : `Hear ${vname}`,
      title: cur && cur.sampleUrl ? vplaying ? "Stop" : "Hear a sample" : "No sample for this voice",
      onClick: () => vplaying ? stopAudio() : playAudio(vkey, cur.sampleUrl)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, vplaying ? "stop" : "play_arrow")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-link accent",
      disabled: busy,
      onClick: () => this.setState({
        voiceOpen: true
      })
    }, "Change"))), /*#__PURE__*/React.createElement("div", {
      className: "sn-row"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-k",
      htmlFor: "sn-captions"
    }, "Captions"), /*#__PURE__*/React.createElement("span", {
      className: "sn-v"
    }, s.captions ? "On" : "Off"), /*#__PURE__*/React.createElement("input", {
      id: "sn-captions",
      className: "sn-check",
      type: "checkbox",
      checked: s.captions,
      disabled: busy,
      onChange: e => this.setState({
        captions: e.target.checked
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "sn-row"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-k",
      htmlFor: "sn-brand"
    }, "Brand kit"), /*#__PURE__*/React.createElement("span", {
      className: "sn-v"
    }, s.brandKit ? "On" : "Off"), /*#__PURE__*/React.createElement("input", {
      id: "sn-brand",
      className: "sn-check",
      type: "checkbox",
      checked: s.brandKit,
      disabled: busy,
      onChange: e => this.setState({
        brandKit: e.target.checked
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "sn-row" + (s.musicOpen ? " stack" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "sn-k"
    }, "Music"), s.musicOpen ? /*#__PURE__*/React.createElement("div", {
      className: "sn-open"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-sub",
      htmlFor: "sn-music"
    }, "Music file link ", /*#__PURE__*/React.createElement("i", null, "an mp3 you have the rights to")), /*#__PURE__*/React.createElement("input", {
      id: "sn-music",
      value: s.musicUrl,
      disabled: busy,
      placeholder: "https://\u2026",
      autoFocus: true,
      onChange: e => this.setState({
        musicUrl: e.target.value
      }),
      style: inp
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-link",
      onClick: () => this.setState({
        musicOpen: false,
        musicUrl: ""
      })
    }, "No music")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "sn-v dim"
    }, "None"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-link accent",
      disabled: busy,
      onClick: () => this.setState({
        musicOpen: true
      })
    }, "Add"))), /*#__PURE__*/React.createElement("div", {
      className: "sn-row stack"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-k",
      htmlFor: "sn-title"
    }, "Title ", /*#__PURE__*/React.createElement("i", null, "optional")), /*#__PURE__*/React.createElement("input", {
      id: "sn-title",
      value: s.title,
      disabled: busy,
      placeholder: "Written from the brief when empty",
      onChange: e => this.setState({
        title: e.target.value
      }),
      style: inp
    })), s.purpose === "walkthrough" ? /*#__PURE__*/React.createElement("div", {
      className: "sn-row stack"
    }, /*#__PURE__*/React.createElement("label", {
      className: "sn-k",
      htmlFor: "sn-product"
    }, "Product ", /*#__PURE__*/React.createElement("i", null, "SKU or product id")), /*#__PURE__*/React.createElement("input", {
      id: "sn-product",
      value: s.productId,
      disabled: busy,
      placeholder: "The narration is written from this product",
      onChange: e => this.setState({
        productId: e.target.value
      }),
      style: inp
    })) : null, /*#__PURE__*/React.createElement("p", {
      className: "sn-foot"
    }, "Sizes, length, voice, captions, brand kit, music and title can all be changed later in the sequence editor. Writing the cut is free; nothing renders until you press Render there."));
  }
  render() {
    const s = this.state;
    const busy = s.busy || !!this.props.busy;
    return /*#__PURE__*/React.createElement("div", {
      className: "sn" + (busy ? " busy" : ""),
      "aria-busy": busy
    }, this.renderTop(busy), this.props.notice ? /*#__PURE__*/React.createElement("div", {
      className: "sn-strip info"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "movie"), /*#__PURE__*/React.createElement("span", null, this.props.notice)) : null, s.err ? /*#__PURE__*/React.createElement("div", {
      className: "sn-strip warn",
      role: "alert"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "info"), /*#__PURE__*/React.createElement("span", null, s.err), /*#__PURE__*/React.createElement("span", {
      className: "sn-sp"
    }), s.credits ? /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(s.credits.buyUrl || api.BUY_URL, "_blank", "noopener")
    }, "Buy credits") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sn-btn sm",
      onClick: () => this.setState({
        err: "",
        credits: null
      })
    }, "Dismiss")) : null, /*#__PURE__*/React.createElement("div", {
      className: "sn-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sn-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sn-brief"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sn-briefhead"
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: "sn-brief"
    }, "What is this video for?"), /*#__PURE__*/React.createElement("span", {
      className: "sn-mute"
    }, api.purposeLabel(s.purpose), ": ", api.PURPOSE_HINT[s.purpose])), /*#__PURE__*/React.createElement("textarea", {
      id: "sn-brief",
      ref: this.briefRef,
      rows: 2,
      value: s.brief,
      disabled: busy,
      placeholder: s.purpose === "walkthrough" ? "Optional for a walkthrough. Anything to stress, e.g. who it is for and what to end on." : "A 30-second promo for our spring launch: show the product in use, warm and plain. End on: shop now.",
      onChange: e => this.setState({
        brief: e.target.value,
        err: /Say what the video/.test(s.err) ? "" : s.err
      })
    })), this.renderGrid(busy), this.renderTray(busy)), this.renderSettings(busy)));
  }
}
export default SequenceCreateTab;